import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus } from '@phosphor-icons/react';

/**
 * SessionForm - Form for creating a single session
 */
export function SessionForm({
  open,
  onOpenChange,
  formData,
  setFormData,
  locations,
  onSubmit
}) {
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    });
  };

  const handleLocationChange = (value) => {
    setFormData({
      ...formData,
      location_id: value
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="btn-primary flex-shrink-0" data-testid="add-session-btn">
          <Plus size={20} weight="bold" className="mr-2" />
          Add Session
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">CREATE NEW SESSION</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Session Title (Optional)</Label>
            <Input
              id="title"
              name="title"
              type="text"
              value={formData.title || ''}
              onChange={handleInputChange}
              placeholder="e.g., STAY ON x Nike Session"
              className="mt-1"
              data-testid="session-title-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Add a custom title for partner sessions</p>
          </div>
          
          <div>
            <Label htmlFor="location_id">Location</Label>
            <Select value={formData.location_id} onValueChange={handleLocationChange}>
              <SelectTrigger className="mt-1" data-testid="location-select">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((location) => (
                  <SelectItem key={location.location_id} value={location.location_id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              name="date"
              type="date"
              value={formData.date}
              onChange={handleInputChange}
              required
              className="mt-1"
              data-testid="session-date-input"
            />
            <p className="text-xs text-muted-foreground mt-1">All times in Pacific Time (PST/PDT)</p>
          </div>
          
          <div>
            <Label htmlFor="start_time">Start Time</Label>
            <Input
              id="start_time"
              name="start_time"
              type="time"
              value={formData.start_time}
              onChange={handleInputChange}
              required
              className="mt-1"
              data-testid="start-time-input"
            />
          </div>
          
          <div>
            <Label htmlFor="end_time">End Time</Label>
            <Input
              id="end_time"
              name="end_time"
              type="time"
              value={formData.end_time}
              onChange={handleInputChange}
              required
              className="mt-1"
              data-testid="end-time-input"
            />
          </div>
          
          <div>
            <Label htmlFor="court">Court/Area (Optional)</Label>
            <Input
              id="court"
              name="court"
              value={formData.court}
              onChange={handleInputChange}
              className="mt-1"
              data-testid="court-input"
              placeholder="e.g., Court A, Main Gym"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="capacity">Capacity</Label>
              <Input
                id="capacity"
                name="capacity"
                type="number"
                min="1"
                max="50"
                value={formData.capacity}
                onChange={handleInputChange}
                required
                className="mt-1"
                data-testid="capacity-input"
              />
            </div>
            <div>
              <Label htmlFor="price">Price</Label>
              <Select
                value={String(formData.price)}
                onValueChange={(value) => setFormData({...formData, price: parseFloat(value)})}
              >
                <SelectTrigger className="mt-1" data-testid="price-select">
                  <SelectValue placeholder="Select price" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Free ($0)</SelectItem>
                  <SelectItem value="10">$10</SelectItem>
                  <SelectItem value="15">$15</SelectItem>
                  <SelectItem value="20">$20</SelectItem>
                  <SelectItem value="25">$25</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label htmlFor="grade_group">Grade Level</Label>
            <Select
              value={formData.grade_group}
              onValueChange={(value) => setFormData({...formData, grade_group: value})}
            >
              <SelectTrigger className="mt-1" data-testid="grade-group-select">
                <SelectValue placeholder="Select grade level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="elementary">Elementary (3rd-5th Grade)</SelectItem>
                <SelectItem value="middle">Middle School (6th-8th Grade)</SelectItem>
                <SelectItem value="high">High School (9th-12th Grade)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Save as Draft Option */}
          <div className="flex items-center space-x-2 pt-2">
            <input
              type="checkbox"
              id="save-as-draft"
              checked={!formData.published}
              onChange={(e) => setFormData({...formData, published: !e.target.checked})}
              className="h-4 w-4 rounded border-gray-300"
              data-testid="save-as-draft-checkbox"
            />
            <Label htmlFor="save-as-draft" className="text-sm text-muted-foreground cursor-pointer">
              Save as draft (hidden from parents until published)
            </Label>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1 btn-primary" data-testid="save-session-btn">
              Create Session
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
